/**
 * The theme tokens the grid has to compute FROM (rather than just hand to
 * CSS): the opaque surface a cell fill composites onto, and the body-text
 * color that fill has to stay legible against — see core/color.ts
 * `readableTextColor`.
 *
 * Read from tokens.css itself rather than duplicated as literals here, so the
 * two can never drift; re-read on every theme change (F-122), which is the
 * only thing that moves them. One `getComputedStyle` per flip, not per cell.
 */
import { useEffect, useState } from "react";

import { primaryThemeRoot, subscribeTheme } from "./theme.js";

export interface ThemeColors {
  /** `--color-surface` — what `.grid-table-box` paints under every cell. */
  surface: string;
  /** `--color-text` — the ink a cell inherits when nothing overrides it. */
  text: string;
}

/**
 * Fallbacks for the light `:root` block, used only where the variables cannot
 * be resolved (jsdom in tests, a paint before the stylesheet lands). Keep in
 * step with tokens.css — but the live read below is what normally decides.
 */
const FALLBACK: ThemeColors = { surface: "#ffffff", text: "#1f2937" };

function readThemeColors(): ThemeColors {
  if (typeof window === "undefined" || typeof window.getComputedStyle !== "function") {
    return FALLBACK;
  }
  // The tokens live on the Kola root element (tokens.css `.kola-root`), not
  // on <html>, so they read the same in the web shell and inside the desktop app.
  const style = window.getComputedStyle(primaryThemeRoot());
  const surface = style.getPropertyValue("--color-surface").trim();
  const text = style.getPropertyValue("--color-text").trim();
  return {
    surface: surface === "" ? FALLBACK.surface : surface,
    text: text === "" ? FALLBACK.text : text,
  };
}

/** Same values → same object, so a theme event that changes nothing (an
 *  explicit choice matching the system one) costs no re-render. */
function keepIfSame(prev: ThemeColors, next: ThemeColors): ThemeColors {
  return prev.surface === next.surface && prev.text === next.text ? prev : next;
}

export function useThemeColors(): ThemeColors {
  const [colors, setColors] = useState<ThemeColors>(readThemeColors);
  useEffect(() => {
    // Re-read once on mount too: initTheme() runs before React boots, but a
    // stylesheet that arrived after the lazy initializer would otherwise leave
    // the fallbacks in place until the first theme flip.
    setColors((prev) => keepIfSame(prev, readThemeColors()));
    return subscribeTheme(() => setColors((prev) => keepIfSame(prev, readThemeColors())));
  }, []);
  return colors;
}
